<template>
  <SmDialog
    v-model:visibility="visibility"
    :close-on-click-modal="false"
    :close-on-press-escape="false">
    <!-- HEADER -->
    <template #title>
      <div class="dialog-header">
        <h2>{{ props.dataSource.name }}: {{ i18n.t('reauthentication') }}</h2>
      </div>
    </template>

    <p>
      {{
        i18n.t('dialogReauthenticateOAuth.text', {
          name: props.dataSource.name,
        })
      }}
    </p>
    <template #footer>
      <div class="sm-dialog__footer">
        <!-- ABORT REAUTHENTICATION -->
        <SmButton outline @click="handleBackToDataSources">
          {{ props.close ? i18n.t('close') : i18n.t('back') }}
        </SmButton>

        <!-- REAUTHENTICATE -->
        <SmButton
          :loading="currentlyCheckingConnection"
          @click="handleAuthentication">
          {{ i18n.t('dialogReauthenticateOAuth.button') }}
        </SmButton>
      </div>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { useRouter } from 'vue-router'

  import {
    ApiError,
    DataSource,
    DataSourceAuthorizationService,
    DataSourcesService,
  } from '@/client'
  import { editToast, sendToast } from '@/components/sm/SmNotification'
  import { useDocumentVisibility } from '@vueuse/core'

  const props = defineProps<{
    dataSource: DataSource
    close?: boolean
  }>()

  const visibility = defineModel('visibility', {
    type: Boolean,
    default: false,
  })
  const router = useRouter()
  const i18n = useI18n()

  const emit = defineEmits(['authentication-completed', 'update:visibility'])

  // ----------------------------------------------------------------------
  // Check connection
  // ----------------------------------------------------------------------

  const currentlyCheckingConnection = ref(false)
  const reauthenticationRequried = ref(false)

  // Check connection if the tab gets the focus
  const tabVisibility = useDocumentVisibility()
  watch(tabVisibility, (current, previous) => {
    if (current === 'visible' && previous === 'hidden') {
      checkConnection()
    }
  })

  onMounted(() => {
    if (props.dataSource.authentication_expired) {
      reauthenticationRequried.value = true
      currentlyCheckingConnection.value = false
    }
  })

  function checkConnection() {
    currentlyCheckingConnection.value = true

    // Manually confirm connection is authenticated
    const notificationID = sendToast(
      i18n.t('dialogReauthenticateOAuth.toastCheckingConnection', {
        name: props.dataSource.name,
      }),
      undefined,
      'loading'
    )

    DataSourcesService.getDataSourceApiV1DataSourceDataSourcesDataSourceIdGet({
      dataSourceId: props.dataSource._id,
    })
      .then(
        (response) => {
          if (!response.authentication_expired) {
            // Connection works
            reauthenticationRequried.value = false

            editToast(notificationID, {
              title: i18n.t('dialogReauthenticateOAuth.toastConnectionWorks', {
                name: props.dataSource.name,
              }),
              type: 'success',
            })

            emit('authentication-completed')
            emit('update:visibility', false)
          } else {
            reauthenticationRequried.value = true
            editToast(notificationID, {
              title: i18n.t('dialogReauthenticateOAuth.toastConnectionFailed', {
                name: props.dataSource.name,
              }),
              type: 'error',
            })
          }
        },
        () => {
          reauthenticationRequried.value = true
          editToast(notificationID, {
            title: i18n.t('dialogReauthenticateOAuth.toastConnectionFailed', {
              name: props.dataSource.name,
            }),
            type: 'error',
          })
        }
      )
      .finally(() => {
        currentlyCheckingConnection.value = false
      })
  }

  watch(
    () => reauthenticationRequried.value,
    () => {
      visibility.value = reauthenticationRequried.value
    },
    { deep: true, immediate: true }
  )

  // ----------------------------------------------------------------------
  // Handle Actions
  // ----------------------------------------------------------------------

  function handleAuthentication() {
    // Get oauth url
    DataSourceAuthorizationService.getReauthorizeOauthUrlApiV1DataSourceOauthReauthorizeDataSourceIdGet(
      {
        dataSourceId: props.dataSource._id,
      }
    ).then(
      (response) => {
        // Open oauth url in new tab
        window.open(response, '_blank')
      },
      // on error
      (error) => {
        sendErrorToast(error)
      }
    )
  }

  function handleBackToDataSources() {
    emit('update:visibility', false)
    if (props.close) return
    router.push({ path: '/data-sources' })
  }

  function sendErrorToast(error: ApiError) {
    let translation
    if (i18n.te(error.body.detail.error + '.title')) {
      translation = i18n.t(error.body.detail.error + '.title')
    } else
      translation = i18n.t('dialogReauthenticateOAuth.toastConnectionFailed')
    sendToast(translation, undefined, 'error')
  }
</script>
<style scoped lang="scss">
  .sm-dialog__footer {
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
  }

  .dialog-header {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 1rem;
  }
  .application-image {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 40px;
    width: 40px;
  }
</style>
